import type { ReactNode } from "react";

/**
 * One step level status line for the create-pool wizard footer.
 *
 * Continue stays disabled while a step is incomplete (ISSUE-927, owner answer C
 * on 2026-09-13), so this line is the only thing that tells a fan why. It is a
 * polite live region: the text changes the moment the gap appears, so a screen
 * reader user hears it without ever reaching the disabled button.
 *
 * The element is rendered even when nothing is missing, holding an empty
 * string. That is what makes the announcement actually happen. A screen reader
 * only announces a live region it was already tracking when the text changed;
 * a region inserted into the DOM together with its first text is commonly
 * missed entirely, which would have left the attributes correct and the
 * behavior absent. Mounting it empty keeps it in the accessibility tree from
 * the first render, so every later change is a change it can see.
 */

export const CREATE_POOL_MISSING_PREFIX = "Still needed: ";
export const CREATE_POOL_MISSING_TEST_ID = "create-pool-missing-summary";
export const CREATE_POOL_MISSING_ELEMENT_ID = "create-pool-missing-summary";

export function createPoolMissingLine(items: readonly string[]): string | null {
  if (items.length === 0) return null;
  return CREATE_POOL_MISSING_PREFIX + items.join(", ") + ".";
}

export type typeCreatePoolMissingSummaryProps = { items: readonly string[] };

export const CreatePoolMissingSummary = (
  props: typeCreatePoolMissingSummaryProps,
): ReactNode => {
  const line = createPoolMissingLine(props.items);
  return (
    <p
      id={CREATE_POOL_MISSING_ELEMENT_ID}
      role="status"
      aria-live="polite"
      data-testid={CREATE_POOL_MISSING_TEST_ID}
      className="text-caption text-ink-secondary"
    >
      {line ?? ""}
    </p>
  );
};
